// ═══ 인보이스 페이지 상태/핸들러 훅 — Invoice.js에서 분리 ═══
// 50+ useState/useEffect/useRef + 모든 핸들러 함수를 캡슐화. 부모는 ctx(c)와 회사 필터 상태만 사용.
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { API, carsUrl, authFetch } from '../utils/api';
import useSettingsFile from '../utils/useSettingsFile';
import { getYearFromVin } from '../utils/vinDecoder';
import { printInvoice as invoicePrintUtil } from '../utils/invoicePrint';
import { pickPriceStr as _pickPriceStr, makeRow as _makeRow, makeEmptyRow as _emptyRow, makeToInvCur, calcTotals, filterInitCars } from '../utils/invoiceCalc';
import { compressFiles } from '../utils/imageCompress';
import { applyOcrResult as _applyOcrResultUtil, handleScreenshot as _handleScreenshotUtil, handleTextPaste as _handleTextPasteUtil } from '../utils/invoiceOcrUtils';
import { saveInvoiceDraft as _saveInvoiceDraftUtil, restoreInvoiceDraft as _restoreInvoiceDraftUtil } from '../utils/invoiceDraftPersistence';
import { forceSend as _forceSendUtil } from '../utils/invoiceSubmit';

// #347 — 선적방식 기본값. 신규 인보이스는 마지막 선택을 기억하고, 기록이 없으면 컨테이너선.
// (기존 인보이스는 loadInvoice 가 저장값을 그대로 쓰고, 빈값 legacy row 는 'roro' 폴백 유지)
var SHIPPING_TYPE_KEY = 'autodoc_invoice_shipping_type';
function loadShippingTypePref(){try{var v=window.localStorage.getItem(SHIPPING_TYPE_KEY);if(v==='roro'||v==='container')return v}catch(e){}return 'container'}
function saveShippingTypePref(v){try{window.localStorage.setItem(SHIPPING_TYPE_KEY,v)}catch(e){}}

export function useInvoiceState(opts) {
  var showToast = opts.showToast;
  var user = opts.user;
  var location = useLocation();
  // location.state는 브라우저 reload(=업체 전환) 후에도 history.state로 보존되므로,
  // 저장된 initCars가 현재 활성 업체와 다른 회사 차량이면 폐기 (cross-tenant prefill 차단)
  var _curUserCid = (user && (user.activeCompanyId || user.companyId)) || 0;
  var _rawInitCars = location.state && location.state.initCars ? location.state.initCars : [];
  var _isDocAgency2 = user && user.role === 'doc_agency';
  var initCars = filterInitCars(_rawInitCars, _curUserCid, _isDocAgency2);
  var isAdmin=user&&(user.role==='관리자'||user.role==='admin');
  var isDocAgency=user&&user.role==='doc_agency';
  var showCompanyFilter=isAdmin||isDocAgency;
  var[filterCompanyId,setFilterCompanyId]=useState((isAdmin||isDocAgency)&&user?(user.activeCompanyId||user.companyId||''):'');
  var[cars,setCars]=useState([]);var[buyers,setBuyers]=useState([]);
  var[shipCo,setShipCo]=useState({companyName:'AUTODEX',addr:'',tel:'',fax:'',bizNo:''});
  var[selBuyer,setSelBuyer]=useState(null);
  var[invoiceNo,setInvoiceNo]=useState('');var[invoiceDate,setInvoiceDate]=useState(new Date().toISOString().slice(0,10));
  var[consignee,setConsignee]=useState('');var[vesselName,setVesselName]=useState('');
  var[exportAgent,setExportAgent]=useState(''); // 신고 366 — 수출대행자 (영문 상호, 한 칸)
  var[fromPort,setFromPort]=useState('INCHEON, KOREA');var[toPort,setToPort]=useState('');
  var[departDate,setDepartDate]=useState('');
  var[bkNo,setBkNo]=useState('');var[containerNo,setContainerNo]=useState('');var[sealNo,setSealNo]=useState('');var[entryPoint,setEntryPoint]=useState('');
  var[freightCurrency,setFreightCurrency]=useState('KRW');
  var[insuranceCurrency,setInsuranceCurrency]=useState('KRW');
  // 순수 함수로 분리됨 → utils/invoiceCalc.js
  // pickPriceStr / mkRow는 currency state에 의존하므로 closure로 wrap
  var pickPriceStr = _pickPriceStr;
  var mkRow = function(c) {
    var _invCur = typeof currency !== 'undefined' && currency ? currency : 'KRW';
    return _makeRow(c, _invCur);
  };
  var[carRows,setCarRows]=useState(function(){return initCars&&initCars.length?initCars.map(mkRow):[mkRow(null)]});
  var[toEmail,setToEmail]=useState('');var[sending,setSending]=useState(false);var[sent,setSent]=useState(false);
  var[shippingType,setShippingType]=useState(loadShippingTypePref());
  // 사용자가 라디오로 바꾼 선택만 기억한다 (loadInvoice/드래프트 복원의 내부 set 은 raw setter 사용)
  var setShippingTypePref=function(v){setShippingType(v);saveShippingTypePref(v)};
  var[currency,setCurrency]=useState('KRW');
  var[incoterms,setIncoterms]=useState('FOB');
  var[freight,setFreight]=useState('');
  var[insurance,setInsurance]=useState('');
  var[carSearch,setCarSearch]=useState('');
  var[carSearchResults,setCarSearchResults]=useState([]);
  var[carSearchShow,setCarSearchShow]=useState(false);
  var[carSearchTimer,setCarSearchTimer]=useState(null);
  var[activeCarSearchRow,setActiveCarSearchRow]=useState(null);// 차량 검색어/드롭다운을 입력 중인 행에만 표기 (행 간 공유 표기 버그 방지)
  var doCarSearch=function(q){
    if(carSearchTimer)clearTimeout(carSearchTimer);
    if(!q||q.length<2){setCarSearchResults([]);setCarSearchShow(false);return;}
    var t=setTimeout(function(){
      authFetch(API+'/cars/search?q='+encodeURIComponent(q)).then(function(r){return r.json()}).then(function(d){setCarSearchResults(d);setCarSearchShow(d.length>0)}).catch(function(){});
    },300);
    setCarSearchTimer(t);
  };
  var[exchangeRate,setExchangeRate]=useState(0);
  var[rateLoading,setRateLoading]=useState(false);
  var[rateEditing,setRateEditing]=useState(false);
  var CURRENCIES=[{code:'USD',symbol:'$'},{code:'KRW',symbol:'₩'},{code:'AED',symbol:'د.إ'},{code:'EUR',symbol:'€'}];
  var INCOTERMS=['FOB','CIF','CFR','EXW','DDP','DAP'];
  var curSymbol=CURRENCIES.find(function(x){return x.code===currency})||CURRENCIES[0];
  // 환율 자동 조회 (USD→KRW 기준 환율 항상 보유)
  var[usdToKrw,setUsdToKrw]=useState(0);
  var fetchRate=function(cur){
    if(cur==='KRW'){setExchangeRate(1);}else{setRateLoading(true);}
    // USD→KRW 환율은 항상 조회 (CFR/CIF 운임 변환용)
    fetch('https://open.er-api.com/v6/latest/USD').then(function(r){return r.json()}).then(function(d){
      if(d.rates&&d.rates.KRW){setUsdToKrw(Math.round(d.rates.KRW*100)/100)}
      if(cur!=='KRW'&&d.rates&&d.rates.KRW){
        if(cur==='USD'){setExchangeRate(Math.round(d.rates.KRW*100)/100)}
      }
      setRateLoading(false);
    }).catch(function(){setRateLoading(false)});
    // USD 외 통화는 별도 조회
    if(cur!=='KRW'&&cur!=='USD'){
      fetch('https://open.er-api.com/v6/latest/'+cur).then(function(r){return r.json()}).then(function(d){
        if(d.rates&&d.rates.KRW){setExchangeRate(Math.round(d.rates.KRW*100)/100)}
      }).catch(function(){});
    }
  };
  useEffect(function(){fetchRate(currency)},[currency]);
  // Method 2: 인보이스 통화 변경 시 모든 행 재동기화 (단일 통화로만 표시)
  // 이미 같은 통화 행은 건드리지 않음 → loadInvoice 직후 사용자 커스텀 가격 보존
  useEffect(function(){
    setCarRows(function(prev){return prev.map(function(r){
      if(r.priceCurrency===currency)return r;
      if(!r.carId)return {...r,priceCurrency:currency};
      return {...r,price:pickPriceStr(r,currency),priceCurrency:currency};
    })});
  },[currency]);
  // toForeign/toKrw 제거 — 미사용 (toInvCur가 모든 통화 변환 담당)
  var[shoringFiles,setShoringFiles]=useState([]);
  // OCR 에 쓴 스크린샷 원본 — 저장 성공 시 첨부서류로 자동 등록 (#311). {file,name} 1건, 마지막 것으로 교체.
  var[pendingOcrFile,setPendingOcrFile]=useState(null);
  var[uploadedDocs,setUploadedDocs]=useState([]);
  // #350 — 최근 사용한 관세사 이메일 (전송칸 datalist/칩 제안). 마운트 + 전송 성공 후 갱신.
  var[recentCustomsEmails,setRecentCustomsEmails]=useState([]);
  var[sendWarnings,setSendWarnings]=useState([]);
  var[savedInvoiceId,setSavedInvoiceId]=useState(null);
  var[invoiceStatus,setInvoiceStatus]=useState('draft');
  // Phase 1.5 #14 파일럿: 낙관적 락 버전 echo. 불러올 때 저장, PUT 시 전송, 409 시 새로고침 안내
  var[invoiceVersion,setInvoiceVersion]=useState(null);
  var[savedList,setSavedList]=useState([]);
  var[confirmAction,setConfirmAction]=useState(null);
  // 신고 368 — 저장 가드에서 중량 누락으로 막힌 차량 행 id 목록 (입력칸 하이라이트용)
  var[weightErrorRowIds,setWeightErrorRowIds]=useState([]);
  var[carDocs,setCarDocs]=useState({});
  // 🔴 SECURITY (Wave 1B, 2026-04-29): signatureUrl 정적 URL → useSettingsFile blob 마이그
  // filterCompanyId 또는 user 활성 회사 기준 — 변경 시 자동 재fetch + 이전 blob revoke.
  var _sigCid=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;
  var _sigFile=useSettingsFile('signature',_sigCid);
  var signatureUrl=_sigFile.url||'';
  var[ssMode,setSsMode]=useState(false);
  var[ssLoading,setSsLoading]=useState(false);
  var[ssResult,setSsResult]=useState(null);
  var[ssImageUrl,setSsImageUrl]=useState('');
  var[ssPreview,setSsPreview]=useState(false);
  var[ssPinned,setSsPinned]=useState(false);
  var[ssEdit,setSsEdit]=useState({});
  var[ssZoom,setSsZoom]=useState(1);
  var[ssDrag,setSsDrag]=useState({dragging:false,startX:0,startY:0,offX:0,offY:0,curX:0,curY:0});
  // #364 — 쇼링 OCR 원본: 이미 저장된 인보이스(savedInvoiceId)면 pending 으로 두지 않고
  // 바로 shoring_list 로 업로드(재진입/텍스트붙여넣기로 유실되는 문제 방지). 없으면 기존처럼 pending.
  var handleOcrOriginal = function(pending) {
    if (savedInvoiceId) {
      uploadInvoiceFiles(savedInvoiceId, [pending.file], 'shoring_list').then(function(result){
        if (result.uploaded > 0) {
          authFetch(API+'/invoices/'+savedInvoiceId).then(function(r){return r.json();}).then(function(inv){
            if (inv && inv.documents) setUploadedDocs(inv.documents);
          }).catch(function(){});
        } else if (result.failed > 0) {
          showToast(result.error?('첨부 업로드 실패: '+result.error):'쇼링 내역서 자동 첨부 실패 — 첨부서류에서 직접 올려주세요','err');
        }
      });
    } else {
      setPendingOcrFile(pending);
    }
  };
  // OCR 핸들러 — utils/invoiceOcrUtils.js 로 분리 (Phase 2 시동, 2026-05-08)
  // deps 로 setter 들 + buyers/mkRow/showToast 주입
  var _ocrDeps = function() { return {
    setSsLoading, setSsResult, setContainerNo, setSealNo, setEntryPoint, setBkNo,
    setVesselName, setToPort, setFromPort, setSelBuyer, setConsignee, setCarRows,
    setSsImageUrl, ssImageUrl, setPendingOcrFile, onOcrOriginal: handleOcrOriginal,
    buyers: buyers, mkRow: mkRow, showToast: showToast
  };};
  var handleScreenshot = function(file) { _handleScreenshotUtil(file, _ocrDeps()); };
  var handleTextPaste = function(text) { _handleTextPasteUtil(text, _ocrDeps()); };
  var ls={display:'block',fontSize:11,fontWeight:600,color:'#64748b',marginBottom:4,marginTop:10};
  var is={width:'100%',padding:'7px 10px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:12,outline:'none',background:'#fff'};
  var is2={padding:'5px 8px',border:'1px solid #d0d5dd',borderRadius:6,fontSize:11,outline:'none',background:'#fff',width:'100%'};

  var loadRecentCustomsEmails=function(){
    return authFetch(API+'/invoices/recent-customs-emails').then(function(r){return r.json()}).then(function(d){
      if(Array.isArray(d))setRecentCustomsEmails(d);
    }).catch(function(){});
  };

  useEffect(function(){
    loadRecentCustomsEmails();
    authFetch(carsUrl(user)).then(function(r){return r.json()}).then(function(d){if(!Array.isArray(d))return;setCars(d);d.forEach(function(c){authFetch(API+'/cars/'+c.id+'/docs').then(function(r2){return r2.json()}).then(function(docs){setCarDocs(function(prev){var n=Object.assign({},prev);n[c.id]=docs;return n})}).catch(function(){})})}).catch(function(){});
    authFetch(API+'/buyers').then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setBuyers(d)}).catch(function(){});
    // 🔴 Wave 1B: signature 로딩은 useSettingsFile hook 으로 자동 처리 (위 _sigFile)
    // 업체 정보 로드
    var cid=filterCompanyId||(user&&(user.activeCompanyId||user.companyId))||0;
    authFetch(API+'/settings/company'+(cid?'?companyId='+cid:'')).then(function(r){return r.json()}).then(function(d){if(d.companyName)setShipCo(d)}).catch(function(){});
    // 저장된 인보이스 목록 로드
    var invUrl=API+'/invoices';if(showCompanyFilter&&filterCompanyId)invUrl+='?companyId='+filterCompanyId;
    authFetch(invUrl).then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setSavedList(d)}).catch(function(){});
    // 마운트시 legacy 글로벌 키 정리
    try{sessionStorage.removeItem('autodoc_invoice_temp')}catch(e){}
    setInvoiceNo('INV-'+new Date().getFullYear()+'-'+String(Math.floor(Math.random()*9000)+1000));
    // 사이드탭 전환 후 복귀: initCars가 비어있으면 회사별 드래프트 복원 (있으면 initCars 우선)
    if(!initCars||initCars.length===0){
      var _cidR=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;
      if(_cidR)restoreInvoiceDraft(_cidR);
    }
  },[]);

  // 언마운트 시 현재 폼을 회사별 sessionStorage에 자동 저장 (사이드탭 전환 후 복귀 보존)
  // ref로 최신 closure를 capture — cleanup이 stale state를 보지 않도록
  var unmountSaveRef=useRef(null);
  unmountSaveRef.current=function(){
    var hasContent=carRows.some(function(r){return r.carNo||r.vin||r.price||r.weight});
    if(!hasContent)return; // 빈 폼이면 저장도 삭제도 하지 않음 (기존 드래프트 보존)
    var cid=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;
    if(cid)saveInvoiceDraft(cid);
  };
  useEffect(function(){return function(){if(unmountSaveRef.current)unmountSaveRef.current()}},[]);

  // 업체 전환 시 filterCompanyId 자동 동기화 (격리 — 옛 회사의 stale state 차단)
  useEffect(function(){
    if(!user)return;
    var newCid=(isAdmin||isDocAgency)?(user.activeCompanyId||user.companyId||''):'';
    setFilterCompanyId(newCid);
  },[user&&user.activeCompanyId,user&&user.companyId]);

  // 드래프트 persistence — utils/invoiceDraftPersistence.js 로 분리 (Phase 2 시동, 2026-05-08)
  var saveInvoiceDraft = function(cid) {
    return _saveInvoiceDraftUtil(cid, {
      carRows: carRows, selBuyer: selBuyer, savedInvoiceId: savedInvoiceId,
      consignee: consignee, vesselName: vesselName, fromPort: fromPort, toPort: toPort, departDate: departDate,
      bkNo: bkNo, containerNo: containerNo, sealNo: sealNo, entryPoint: entryPoint,
      currency: currency, exchangeRate: exchangeRate, incoterms: incoterms,
      freight: freight, insurance: insurance, freightCurrency: freightCurrency, insuranceCurrency: insuranceCurrency,
      shippingType: shippingType, toEmail: toEmail, invoiceNo: invoiceNo, invoiceDate: invoiceDate,
      pendingOcrFile: pendingOcrFile
    });
  };
  var restoreInvoiceDraft = function(cid) {
    return _restoreInvoiceDraftUtil(cid, {
      setCarRows: setCarRows, setSelBuyer: setSelBuyer, setConsignee: setConsignee, setVesselName: setVesselName,
      setFromPort: setFromPort, setToPort: setToPort, setDepartDate: setDepartDate, setBkNo: setBkNo,
      setContainerNo: setContainerNo, setSealNo: setSealNo, setEntryPoint: setEntryPoint,
      setCurrency: setCurrency, setExchangeRate: setExchangeRate, setIncoterms: setIncoterms,
      setFreight: setFreight, setInsurance: setInsurance,
      setFreightCurrency: setFreightCurrency, setInsuranceCurrency: setInsuranceCurrency,
      setShippingType: setShippingType, setToEmail: setToEmail,
      setInvoiceNo: setInvoiceNo, setInvoiceDate: setInvoiceDate, setSavedInvoiceId: setSavedInvoiceId,
      setPendingOcrFile: setPendingOcrFile,
      loadInvoice: loadInvoice, showToast: showToast
    });
  };

  // filterCompanyId 변경 시 인보이스 목록 + Shipper 정보 + 차량 목록 재로드
  // 마운트 시점에는 stale state 클리어를 skip — 마운트 useEffect(L143)와 initCars 폼 prefill을 보존
  var prevFilterCompanyIdRef=useRef(filterCompanyId);
  useEffect(function(){
    if(!showCompanyFilter||!filterCompanyId)return;
    var isRealChange=prevFilterCompanyIdRef.current!==filterCompanyId;
    prevFilterCompanyIdRef.current=filterCompanyId;
    if(isRealChange){
      // 격리: 회사가 실제로 바뀌었을 때만 옛 회사 차량/인보이스 즉시 비우기
      setCars([]);
      setSavedList([]);
      setShoringFiles([]);
      setPendingOcrFile(null);
      setUploadedDocs([]);
      setSavedInvoiceId(null);
      // 새 회사 드래프트 복원 시도, 없으면 빈 폼
      var restored=restoreInvoiceDraft(filterCompanyId);
      if(!restored)setCarRows([mkRow(null)]);
    }
    var invUrl=API+'/invoices?companyId='+filterCompanyId;
    authFetch(invUrl).then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setSavedList(d)}).catch(function(){});
    authFetch(API+'/settings/company?companyId='+filterCompanyId).then(function(r){return r.json()}).then(function(d){if(d.companyName)setShipCo(d)}).catch(function(){});
    authFetch(API+'/cars?companyId='+filterCompanyId).then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setCars(d)}).catch(function(){});
  },[filterCompanyId]);

  var resetForm=function(){setCarRows([mkRow(null)]);setSelBuyer(null);setConsignee('');setVesselName('');setExportAgent('');setToPort('');setDepartDate('');setBkNo('');setContainerNo('');setSealNo('');setEntryPoint('');setInvoiceNo('INV-'+new Date().getFullYear()+'-'+String(Math.floor(Math.random()*9000)+1000));setShoringFiles([]);setPendingOcrFile(null);setUploadedDocs([]);setWeightErrorRowIds([])};

  // 인보이스 삭제 (임시저장 + 발송완료/발송대기 모두 허용)
  //  - 함수명은 호환을 위해 deleteDraft 유지하나 draft 외 상태도 삭제 가능
  //  - 발송완료(sent): 관세사 전송 이력이 있어 차량 상태(면장신청 이상)는 자동 복구 안 됨 → 경고 강화
  //  - 현재 편집 중인 인보이스 삭제 시 폼 리셋
  //  - 삭제 후 savedList 재조회
  var deleteDraft=function(id){
    if(!id)return;
    var target=(savedList||[]).find(function(inv){return inv.id===id});
    if(!target){showToast('인보이스를 찾을 수 없습니다','err');return;}
    // 상태 라벨: invoice_status sent/ready 외(NULL 포함)는 임시저장(draft)으로 간주
    var isSent=target.invoice_status==='sent';
    var statusLabel=isSent?'발송완료':target.invoice_status==='ready'?'발송대기':'임시저장';
    var warnMsg=isSent
      ?'#'+id+' 발송완료 인보이스를 삭제할까요? 관세사 전송 이력이 있으며, 차량 상태는 자동 복구되지 않습니다. 복구할 수 없습니다.'
      :'#'+id+' '+statusLabel+' 인보이스를 삭제할까요? 복구할 수 없습니다.';
    setConfirmAction({
      title:statusLabel+' 삭제',
      msg:warnMsg,
      type:'delete',
      fn:function(){
        authFetch(API+'/invoices/'+id,{method:'DELETE'}).then(function(r){return r.json()}).then(function(d){
          if(d.success){
            showToast('삭제 완료');
            if(savedInvoiceId===id){
              setSavedInvoiceId(null);
              resetForm();
            }
            authFetch(API+'/invoices').then(function(r){return r.json()}).then(function(d2){if(Array.isArray(d2))setSavedList(d2)}).catch(function(){});
          } else {
            showToast('삭제 실패: '+(d.error||''),'err');
          }
        }).catch(function(){showToast('서버 오류','err')});
      }
    });
  };
  // #348 — 첨부서류 개별 삭제. 성공 시 uploadedDocs 에서 즉시 제거 (체크리스트 건수도 함께 갱신).
  var deleteUploadedDoc=function(docId){
    if(!docId)return Promise.resolve();
    return authFetch(API+'/invoice-documents/'+docId,{method:'DELETE'}).then(function(r){return r.json()}).then(function(d){
      if(d&&d.success){
        setUploadedDocs(function(prev){return (prev||[]).filter(function(x){return x.id!==docId})});
        showToast('첨부서류 삭제 완료');
      } else {
        showToast('첨부서류 삭제 실패: '+((d&&d.error)||''),'err');
      }
    }).catch(function(){showToast('서버 오류','err')});
  };

  // #363 — 서버 multer 상한(10개/요청)과 무관하게 항상 10개씩 순차 업로드.
  // 압축 → 청크 분할 → 순차 POST. 청크 실패해도 나머지는 계속 시도(부분 성공 반영).
  // failedFiles: 실패한 청크에 속한 "압축 전" 원본 File — 호출자가 실패분만 재시도 큐에 남기게 한다
  // (성공분 재업로드로 인한 중복 등록 방지).
  var uploadInvoiceFiles = async function(invId, files, docType){
    var list=files&&files.length?Array.from(files):[];
    if(!list.length)return{ok:true,uploaded:0,failed:0,error:null,failedFiles:[]};
    var compressed=await compressFiles(list);
    var chunks=[];
    for(var i=0;i<compressed.length;i+=10)chunks.push({compressed:compressed.slice(i,i+10),original:list.slice(i,i+10)});
    var uploaded=0,failed=0,error=null,failedFiles=[];
    for(var ci=0;ci<chunks.length;ci++){
      var chunk=chunks[ci];
      try{
        var fd=new FormData();fd.append('docType',docType);
        chunk.compressed.forEach(function(f){fd.append('files',f)});
        var resp=await authFetch(API+'/invoices/'+invId+'/upload',{method:'POST',body:fd});
        var data=await resp.json();
        if(data&&data.success){uploaded+=chunk.compressed.length;}
        else{failed+=chunk.compressed.length;failedFiles=failedFiles.concat(chunk.original);if(!error)error=(data&&data.error)||null;}
      }catch(e){failed+=chunk.compressed.length;failedFiles=failedFiles.concat(chunk.original);if(!error)error=(e&&e.message)||null;}
    }
    return{ok:failed===0,uploaded:uploaded,failed:failed,error:error,failedFiles:failedFiles};
  };

  var handleFilesAdded = async function(files){
    if(!files||files.length===0)return;
    var invId=savedInvoiceId;
    if(!invId){
      // 순서 무관 첨부: 신규 인보이스 + DB 매칭 차량 0개면 아직 저장할 인보이스가 없으므로
      // 에러 폐기 대신 shoringFiles 큐에 쌓아둠 → 차량 추가 후 저장(saveDraft/관세사 전송) 시 함께 업로드.
      // ("차량 없는 인보이스 저장 불가" 제약은 saveInvoiceToDb 에 그대로 유지)
      var matchedCount=0;
      for(var ri=0;ri<carRows.length;ri++){var rr=carRows[ri];if(cars.find(function(cc){return cc.vin===rr.vin||cc.carNo===rr.carNo})){matchedCount++}}
      if(matchedCount===0){
        setShoringFiles(function(prev){return prev.concat(Array.from(files))});
        showToast('첨부 '+files.length+'건 대기 — 차량 추가 후 저장 시 함께 업로드됩니다');
        return;
      }
      invId=await saveInvoiceToDb();
      if(!invId){setShoringFiles(function(prev){return prev.concat(Array.from(files))});return}
      setInvoiceStatus('draft');
      authFetch(API+'/invoices').then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setSavedList(d)}).catch(function(){});
    }
    var result=await uploadInvoiceFiles(invId,files,'shoring_photo');
    if(result.uploaded>0){
      var invResp=await authFetch(API+'/invoices/'+invId);
      var inv=await invResp.json();
      if(inv&&inv.documents)setUploadedDocs(inv.documents);
      showToast('\u2705 \uCCA8\uBD80\uD30C\uC77C '+result.uploaded+'\uAC74 \uC5C5\uB85C\uB4DC \uC644\uB8CC');
    }
    if(result.failed>0){
      showToast(result.error?('\uCCA8\uBD80 \uC5C5\uB85C\uB4DC \uC2E4\uD328: '+result.error):'\uCCA8\uBD80\uD30C\uC77C \uC5C5\uB85C\uB4DC \uC2E4\uD328','err');
    }
  };
  var addRow=function(){setCarRows(function(p){return p.concat(_emptyRow())});};
  var removeRow=function(id){setCarRows(function(p){return p.filter(function(r){return r.id!==id})});};
  var updateRow=function(id,field,val){
    setCarRows(function(p){return p.map(function(r){return r.id===id?{...r,[field]:val}:r})});
    if(field==='weight')setWeightErrorRowIds(function(p){return (p||[]).filter(function(x){return x!==id})});
  };
  var onCarSelect=function(rowId,carOrId){
    // 검색 결과 객체를 직접 받거나(우선) 또는 id로 cars state에서 lookup(폴백)
    var c=(carOrId&&typeof carOrId==='object')?carOrId:cars.find(function(x){return x.id===parseInt(carOrId)});
    if(!c)return;
    var krw=c.buyPrice||0;
    var _vy2=getYearFromVin(c.vin,c.regDate?parseInt(c.regDate.slice(0,4)):null);var _yr2=_vy2?String(_vy2):(c.regDate?c.regDate.slice(0,4):'');
    var cache={_carInvAmt:c.invoice_amount>0?c.invoice_amount:0,_carUsd:c.sell_price_usd>0?c.sell_price_usd:0,_carKrw:krw};
    var newPrice=pickPriceStr(cache,currency);
    setCarRows(function(p){return p.map(function(r){return r.id===rowId?{...r,carId:c.id,carNo:c.carNo||'',carType:c.carType||'',year:_yr2,vin:c.vin||'',priceKrw:krw,_carInvAmt:cache._carInvAmt,_carUsd:cache._carUsd,_carKrw:cache._carKrw,price:newPrice,priceCurrency:currency}:r})});
    if(c.buyerId&&!selBuyer){var b=buyers.find(function(x){return x.id===c.buyerId});if(b)setSelBuyer(b);}
  };
  // onPhotoChange/onPhotosChange/removePhoto 제거 — 자식 컴포넌트에서 미사용
  // 통화 변환 + 합계 계산 → utils/invoiceCalc.js
  var toInvCur = makeToInvCur(currency, exchangeRate, usdToKrw);
  var _totals = calcTotals(carRows, freight, freightCurrency, insurance, insuranceCurrency, currency, toInvCur);
  var totalAmt = _totals.totalAmt;
  var totalFreightInv = _totals.totalFreightInv;
  var totalInsuranceInv = _totals.totalInsuranceInv;
  var totalWeight = _totals.totalWeight;
  var grandTotal = _totals.grandTotal;

  // printInvoice → utils/invoicePrint.js로 이동. 호출 시점에 현재 ctx 스냅샷을 전달.
  var printInvoice=function(){
    invoicePrintUtil({
      curSymbol:curSymbol,carRows:carRows,selBuyer:selBuyer,consignee:consignee,
      signatureUrl:signatureUrl,shipCo:shipCo,invoiceNo:invoiceNo,invoiceDate:invoiceDate,
      vesselName:vesselName,fromPort:fromPort,toPort:toPort,departDate:departDate,
      bkNo:bkNo,containerNo:containerNo,sealNo:sealNo,entryPoint:entryPoint,
      incoterms:incoterms,currency:currency,exchangeRate:exchangeRate,
      totalAmt:totalAmt,totalFreightInv:totalFreightInv,totalInsuranceInv:totalInsuranceInv,
      totalWeight:totalWeight,grandTotal:grandTotal,toInvCur:toInvCur
    });
  };

  // 인보이스 DB 저장 (공통)
  var[savingDraft,setSavingDraft]=useState(false);
  var saveInvoiceToDb = async function(){
    if(!carRows.length){showToast('차량을 추가하세요','err');return null;}
    // 2026-05-01: 자유 텍스트 흐름 fallback — selBuyer.id 또는 consignee 텍스트 둘 중 하나 있으면 통과
    // 백엔드 invoices-crud.js POST/PUT 가드와 정합 (consigneeName fallback)
    if((!selBuyer||!selBuyer.id) && !(consignee||'').trim()){showToast('바이어 또는 수하인 이름을 입력해주세요','err');return null;}
    var carIds=[];var carFobs=[];var carWeights=[];var carCurrencies=[];
    for(var ri=0;ri<carRows.length;ri++){
      var matched=cars.find(function(c){return c.vin===carRows[ri].vin||c.carNo===carRows[ri].carNo});
      if(matched){carIds.push(matched.id);carFobs.push(parseInt(String(carRows[ri].price).replace(/[^0-9]/g,''))||0);carWeights.push(carRows[ri].weight||'');carCurrencies.push(carRows[ri].priceCurrency||currency);}
    }
    if(carIds.length===0){showToast('DB에 매칭되는 차량이 없습니다','err');return null;}
    // Kyle 2026-05-04 — 필수 5 필드 사전 가드 (백엔드 400 도달 전)
    var hasZeroFob=false;for(var fi=0;fi<carFobs.length;fi++){if(!(carFobs[fi]>0)){hasZeroFob=true;break;}}
    if(hasZeroFob){showToast('모든 차량의 단가를 입력해주세요','err');return null;}
    // 신고 368 — 중량 가드는 저장이 아니라 관세사 전송 단계(sendToCustoms)에만 있다.
    //   Jin 결정 2026-09-18: 중량 빈 기존 인보이스가 운영에 다수라 저장까지 막으면 수정 자체가 불가.
    if(currency!=='KRW' && (parseFloat(exchangeRate)||0)<=0){showToast('외화 인보이스는 환율을 입력해주세요','err');return null;}
    if(!(toPort||'').trim()){showToast('도착항을 입력해주세요','err');return null;}
    // shippingType==='container' 일 때만 컨테이너번호/씰번호 가드 (Kyle 2026-05-05 RORO 면제)
    if(shippingType==='container'){
      if(!(containerNo||'').trim()){showToast('컨테이너번호를 입력해주세요','err');return null;}
      if(!(sealNo||'').trim()){showToast('씰번호를 입력해주세요','err');return null;}
    }
    // #351: 반입지 가드는 저장이 아니라 관세사 전송 단계에서만 (첨부 자동저장/임시저장이 막히던 문제)
    // 명시적 companyId 전송 — 다중업체/탭 전환 시 stale activeCompanyId로 인한 wrong-company 저장 방지
    var explicitCid=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;
    var payload={
      buyerId:selBuyer?selBuyer.id:0, fobPrice:totalAmt, currency:currency, paymentTerms:'T/T',
      shippingPort:fromPort, destinationPort:toPort, shipDate:departDate,
      shippingType:shippingType, purchaseType:'individual',
      customsEmail:toEmail, memo:'', carIds:carIds, carFobs:carFobs, carWeights:carWeights, carCurrencies:carCurrencies,
      exchangeRate:exchangeRate, amountForeign:totalAmt, incoterms:incoterms,
      freight:totalFreightInv, insurance:totalInsuranceInv,
      bkNo:bkNo, containerNo:containerNo, sealNo:sealNo, entryPoint:entryPoint,
      consigneeName:consignee||(selBuyer?selBuyer.name:''), consigneeAddr:selBuyer?selBuyer.addr||'':'',
      exportAgent:exportAgent,
      companyId:explicitCid
    };
    var saveUrl=savedInvoiceId?API+'/invoices/'+savedInvoiceId:API+'/invoices';
    var saveMethod=savedInvoiceId?'PUT':'POST';
    // Phase 1.5 #14: PUT 경로에서 낙관적 락 version echo (현재 불러온 버전 전송)
    if(saveMethod==='PUT'&&invoiceVersion!=null)payload.version=invoiceVersion;
    var saveResp=await authFetch(saveUrl,{method:saveMethod,headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
    // 409 Conflict — CAR_ALREADY_MAPPED (Phase 2D 미들웨어) 와 optimistic lock 분리 처리
    if(saveResp.status===409){
      var conflictData=await saveResp.json().catch(function(){return{}});
      if(conflictData.error==='CAR_ALREADY_MAPPED'){
        var dupList=(conflictData.cars||[]).map(function(c){return (c.carNo||'#'+c.carId)+' → 인보이스 #'+c.invoiceId}).join(', ');
        showToast('⚠️ 이미 다른 인보이스에 등록된 차량: '+(dupList||(conflictData.carIds||[]).join(','))+'. 해당 인보이스에서 먼저 제외 후 다시 시도해주세요.','err');
      } else {
        showToast('⚠️ '+(conflictData.message||conflictData.error||'다른 사용자가 먼저 저장했습니다. 새로고침 후 다시 시도해주세요.'),'err');
      }
      return null;
    }
    var saveData=await saveResp.json();
    if(!saveData.success){showToast(saveData.error||'인보이스 저장 실패','err');return null;}
    var resultId=savedInvoiceId||saveData.id;
    setSavedInvoiceId(resultId);
    // 대기 첨부 flush — 수동 큐(shoringFiles) + OCR 원본(pendingOcrFile).
    // 저장 경로가 여러 곳(임시저장/관세사 전송/카톡 공유/첨부 드롭 자동저장)이라 저장 코어에서
    // 한 번만 처리하고, 업로드 성공분은 즉시 비운다. (기존엔 saveDraft/sendToCustoms 가 각각
    // 올리면서 큐를 안 비워, 임시저장을 두 번 누르면 같은 파일이 중복 등록됐다.)
    // 실패해도 인보이스 저장은 유효 — 안내만 하고 대기 상태를 남겨 다음 저장에서 재시도.
    if(shoringFiles.length>0){
      var qResult=await uploadInvoiceFiles(resultId,shoringFiles,'shoring_photo');
      if(qResult.ok){setShoringFiles([]);}
      else{
        // 실패분만 큐에 남긴다 — 성공분까지 남기면 다음 저장에서 중복 등록된다.
        setShoringFiles(qResult.failedFiles);
        showToast((qResult.error?('첨부 업로드 실패: '+qResult.error):'첨부파일 업로드 실패 — 첨부서류에서 다시 올려주세요')+' (성공 '+qResult.uploaded+'장 / 실패 '+qResult.failed+'장)','err');
      }
    }
    // #364 — 쇼링 OCR 원본은 docType 'shoring_list'(앱과 동일, 메일 라벨 '쇼링리스트')로 자동첨부.
    if(pendingOcrFile&&pendingOcrFile.file){
      var ocrResult=await uploadInvoiceFiles(resultId,[pendingOcrFile.file],'shoring_list');
      if(ocrResult.failed===0){setPendingOcrFile(null);}
      else{showToast(ocrResult.error?('첨부 업로드 실패: '+ocrResult.error):'스크린샷 자동 첨부 실패 — 첨부서류에서 직접 올려주세요','err');}
    }
    // Phase 1.5 #14: 저장 성공 후 최신 version 재조회 (이후 편집 시 stale 409 방지)
    if(saveMethod==='PUT'&&invoiceVersion!=null)setInvoiceVersion(invoiceVersion+1);
    // 저장 성공 시 현재 회사 드래프트 캐시 제거 (다음 진입 시 stale 복원 방지)
    try{var _doneCid=parseInt(filterCompanyId)||(user&&(user.activeCompanyId||user.companyId))||0;if(_doneCid)sessionStorage.removeItem('autodoc_invoice_temp_'+_doneCid)}catch(e){}
    return resultId;
  };

  // 임시저장 (DB 저장만, 전송 안 함)
  // 저장된 인보이스 불러오기
  var loadInvoice = function(invId){
    authFetch(API+'/invoices/'+invId).then(function(r){return r.json()}).then(function(inv){
      if(!inv||!inv.id)return;
      setSavedInvoiceId(inv.id);
      setInvoiceStatus(inv.invoice_status||'draft');
      // Phase 1.5 #14: 낙관적 락 버전 저장 (PUT 시 echo)
      setInvoiceVersion(typeof inv.version==='number'?inv.version:(parseInt(inv.version)||null));
      setToPort(inv.destinationPort||'');setFromPort(inv.shippingPort||'INCHEON, KOREA');
      setDepartDate(inv.shipDate||'');setShippingType(inv.shippingType||'roro');
      setCurrency(inv.currency||'USD');setExchangeRate(inv.exchangeRate||0);
      setIncoterms(inv.incoterms||'FOB');
      setBkNo(inv.bk_no||'');setContainerNo(inv.container_no||'');setSealNo(inv.seal_no||'');setEntryPoint(inv.entry_point||'');
      setToEmail(inv.customsEmail||'');
      setConsignee(inv.consigneeName||'');
      setExportAgent(inv.export_agent||'');
      if(inv.buyerId){var b=buyers.find(function(x){return x.id===inv.buyerId});if(b)setSelBuyer(b)}
      if(inv.cars&&inv.cars.length>0){
        var _invCur=inv.currency||'USD';
        setCarRows(inv.cars.map(function(c){
          var _vy3=getYearFromVin(c.vin,c.regDate?parseInt(c.regDate.slice(0,4)):null);
          // 차량 메타 (cars state) 에서 _car* 캐시 보강 — 통화 전환 시 재계산용
          var matched=cars.find(function(x){return x.id===(c.carId||0)});
          var _invAmt=matched&&matched.invoice_amount>0?matched.invoice_amount:0;
          var _usd=matched&&matched.sell_price_usd>0?matched.sell_price_usd:0;
          var _krw=matched?matched.buyPrice||0:(c.buyPrice||0);
          return{id:c.carId||Date.now(),carId:c.carId||0,carNo:c.carNo||'',carType:c.carType||'',year:_vy3?String(_vy3):(c.regDate?c.regDate.slice(0,4):''),vin:c.vin||'',price:String(c.fobPrice||0),priceCurrency:c.currency||_invCur,_carInvAmt:_invAmt,_carUsd:_usd,_carKrw:_krw,weight:c.weight||'',photo:null,photoUrl:''};
        }));
      }
      setShoringFiles([]);
      setPendingOcrFile(null);
      setUploadedDocs(Array.isArray(inv.documents)?inv.documents:[]);
      setWeightErrorRowIds([]);
    }).catch(function(){});
  };

  var saveDraft = async function(){
    setSavingDraft(true);
    var invId=null;
    try {
      invId=await saveInvoiceToDb();
      if(invId){
        setInvoiceStatus('draft');
        authFetch(API+'/invoices').then(function(r){return r.json()}).then(function(d){if(Array.isArray(d))setSavedList(d)}).catch(function(){});
      }
    } catch(e){}
    setSavingDraft(false);
    return invId; // 성공: invId / 실패: null — 호출자(카톡공유)가 성공 여부 판단 (watcher 제거)
  };

  // 인보이스 저장 → 추가서류 업로드 → 관세사 전송
  var sendToCustoms = async function(){
    if(!carRows.length){showToast('차량을 추가하세요','err');return;}
    if(!toEmail){showToast('관세사 이메일을 입력하세요','err');return;}
    // #351 — 반입지는 관세사 전송 시점에만 필수 (임시저장/첨부 자동저장은 통과)
    if(!(entryPoint||'').trim()){showToast('반입지(Entry Point)를 입력해주세요','err');return;}
    // 신고 368 — 중량은 전송 시점에만 필수 (Jin 결정 2026-09-18: 저장은 허용, 전송만 차단).
    // 판정은 서버 utils/invoiceWeight.js 와 동일: trim 비어있지 않고 parseInt>0.
    // 놓치기 쉬운 토스트 대신 확인 모달로 표시 + 비어있는 차량 행을 하이라이트(weightErrorRowIds).
    var emptyWeightRowsSend=[];
    for(var sri=0;sri<carRows.length;sri++){
      var matchedSend=cars.find(function(c){return c.vin===carRows[sri].vin||c.carNo===carRows[sri].carNo});
      if(!matchedSend)continue; // saveInvoiceToDb 의 매칭 로직과 동일 — DB 미매칭 행은 인보이스에 포함되지 않는다
      var wvSend=String(carRows[sri].weight||'').trim();
      if(!(wvSend&&parseInt(wvSend)>0))emptyWeightRowsSend.push(carRows[sri]);
    }
    if(emptyWeightRowsSend.length>0){
      setWeightErrorRowIds(emptyWeightRowsSend.map(function(r){return r.id}));
      var missingLabelSend=emptyWeightRowsSend.map(function(r){return r.carNo||r.vin||'-'}).join(', ');
      setConfirmAction({type:'danger',title:'전송할 수 없습니다',msg:'모든 차량의 중량(KGS)을 입력해주세요 — 비어 있는 차량: '+missingLabelSend,hideCancel:true,fn:function(){}});
      return;
    }
    setWeightErrorRowIds([]);
    setSending(true);setSendWarnings([]);
    try {
      var invId=await saveInvoiceToDb();
      if(!invId){setSending(false);return;}

      // 관세사 전송 (대기 첨부는 saveInvoiceToDb 에서 이미 업로드됨)
      var sendResp = await authFetch(API+'/invoices/send-customs',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({invoiceId:invId,toEmail:toEmail})});
      var sendData = await sendResp.json();

      if(sendData.warnings && sendData.warnings.length>0 && !sendData.success){
        setSendWarnings(sendData.warnings);
        setSending(false);
        showToast('누락 서류가 있습니다. 확인 후 강제 전송하세요.','err');
        return;
      }
      // 백엔드가 success:false 또는 HTTP 4xx/5xx로 응답한 경우 (예: 첨부 용량 초과, SMTP 에러, 중량 가드)
      if(!sendResp.ok || sendData.success===false || sendData.error){
        setSending(false);
        var _sendErr=sendData.error||'전송 실패';
        // 신고 368 — 서버 중량 가드 400 도 클라 가드와 동일하게 모달로 노출 (토스트는 놓치기 쉬움)
        if(/중량/.test(_sendErr)){setConfirmAction({type:'danger',title:'전송할 수 없습니다',msg:_sendErr,hideCancel:true,fn:function(){}});}
        else{showToast(_sendErr,'err');}
        return;
      }
      setSending(false);setSent(true);setInvoiceStatus('sent');
      authFetch(API+'/invoices/'+invId+'/status',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({invoice_status:'sent'})}).catch(function(){});
      try{sessionStorage.removeItem('autodoc_invoice_temp')}catch(e){}
      loadRecentCustomsEmails(); // #350 — 방금 쓴 주소를 제안 목록 맨 앞으로
      showToast('✅ 관세사 전송 완료! (첨부 '+sendData.attachments+'건) → 면장신청 자동진입');
    } catch(err){
      setSending(false);showToast('전송 실패: '+(err.message||'서버오류'),'err');
    }
  };

  // forceSend — utils/invoiceSubmit.js 로 분리 (Phase 2 시동, 2026-05-08)
  // saveDraft / sendToCustoms 는 saveInvoiceToDb helper 30+ state 결합 → 옵션 A 진입 시 처리 (백로그)
  var forceSend = function() {
    return _forceSendUtil({
      savedInvoiceId: savedInvoiceId, toEmail: toEmail,
      setSending: setSending, setSent: setSent, setSendWarnings: setSendWarnings,
      showToast: showToast
    });
  };

  // ─── ctx 객체 (자식 컴포넌트로 전달할 모든 상태/핸들러) ───
  var c = {
    ls,is,is2,carRows,weightErrorRowIds,currency,curSymbol,totalAmt,totalFreightInv,totalInsuranceInv,
    totalWeight,grandTotal,toInvCur,
    ssMode,setSsMode,ssLoading,ssResult,setSsResult,ssImageUrl,setSsImageUrl,handleScreenshot,handleTextPaste,
    ssPinned,setSsPinned,ssEdit,setSsEdit,ssPreview,setSsPreview,ssZoom,setSsZoom,ssDrag,setSsDrag,
    buyers,setSelBuyer,setConsignee,setContainerNo,setSealNo,setBkNo,setEntryPoint,setVesselName,
    setToPort,setFromPort,showToast,
    invoiceNo,setInvoiceNo,invoiceDate,setInvoiceDate,consignee,exportAgent,setExportAgent,selBuyer,vesselName,fromPort,toPort,
    departDate,setDepartDate,bkNo,containerNo,sealNo,entryPoint,
    CURRENCIES,INCOTERMS,setCurrency,setIncoterms,rateEditing,setRateEditing,
    exchangeRate,setExchangeRate,rateLoading,fetchRate,incoterms,
    freight,setFreight,freightCurrency,setFreightCurrency,usdToKrw,
    insurance,setInsurance,insuranceCurrency,setInsuranceCurrency,
    resetForm,addRow,removeRow,updateRow,onCarSelect,
    carSearch,setCarSearch,doCarSearch,carSearchResults,setCarSearchResults,
    carSearchShow,setCarSearchShow,activeCarSearchRow,setActiveCarSearchRow,shippingType,
    setShippingType:setShippingTypePref,
    shoringFiles,setShoringFiles,pendingOcrFile,setPendingOcrFile,handleFilesAdded,uploadedDocs,setUploadedDocs,deleteUploadedDoc,cars,carDocs,setCarDocs,shipCo,signatureUrl,
    savedList,savedInvoiceId,setSavedInvoiceId,loadInvoice,deleteDraft,
    invoiceStatus,toEmail,setToEmail,recentCustomsEmails,savingDraft,sending,sent,sendWarnings,setSendWarnings,
    confirmAction,setConfirmAction,saveDraft,printInvoice,sendToCustoms,forceSend
  };

  return { c: c, showCompanyFilter: showCompanyFilter, filterCompanyId: filterCompanyId, setFilterCompanyId: setFilterCompanyId, saveInvoiceDraft: saveInvoiceDraft };
}
